import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth, canVendorAccessPortal } from "../../context/AuthContext";
import { useGoogleLogin } from "@react-oauth/google";
import { isValidEmail } from "../../utils/validators";
import {
  googleLoginVendorService,
  loginVendorService,
} from "../../services/vendorAuthService";
import { useFeatureFlags } from "../../context/FeatureFlagsContext";

const googleClientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;

const VendorLogin = () => {
  const [form, setForm] = useState({ email: "", password: "" });
  const navigate = useNavigate();
  const { login } = useAuth();
  const { googleLoginVendorEnabled } = useFeatureFlags();

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (!isValidEmail(form.email)) {
        alert("Please enter a valid email address.");
        return;
      }
      const data = await loginVendorService(form);
      if (!canVendorAccessPortal(data.vendor)) {
        alert(
          "This vendor account cannot access the portal. If you think this is wrong, contact support.",
        );
        return;
      }
      login(data.vendor, data.token);
      navigate("/vendor/dashboard", { replace: true });
    } catch (err) {
      alert(err.message);
    }
  };

  const handleGoogleSuccess = async (tokenResponse) => {
    try {
      const data = await googleLoginVendorService(tokenResponse.access_token);
      if (!canVendorAccessPortal(data.vendor)) {
        alert(
          "This vendor account cannot access the portal. If you think this is wrong, contact support.",
        );
        return;
      }
      login(data.vendor, data.token);
      navigate("/vendor/dashboard", { replace: true });
    } catch (error) {
      console.error("Google login error:", error);
      alert(error.message || "Google login failed");
    }
  };

  const googleLogin = useGoogleLogin({
    onSuccess: handleGoogleSuccess,
    onError: () => alert("Google login failed"),
    flow: "implicit",
  });

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-white to-gray-100">
      <div className="w-full max-w-md bg-white shadow-xl rounded-xl p-8">
        <h2 className="text-2xl font-bold text-center text-gray-800 mb-6">
          Vendor Login
        </h2>

        <form onSubmit={handleSubmit} className="space-y-4" autoComplete="on">
          <input
            className="w-full p-3 border rounded"
            type="email"
            name="email"
            id="mmd-vendor-email"
            autoComplete="username"
            placeholder="Email"
            value={form.email}
            onChange={handleChange}
            required
          />
          <input
            className="w-full p-3 border rounded"
            type="password"
            name="password"
            id="mmd-vendor-password"
            autoComplete="current-password"
            placeholder="Password"
            value={form.password}
            onChange={handleChange}
            required
          />
          <button
            className="w-full p-3 bg-rose-600 text-white rounded hover:bg-rose-700"
            type="submit"
          >
            Login
          </button>
        </form>

        {googleClientId && googleLoginVendorEnabled ? (
          <>
            <div className="flex items-center gap-3 my-4">
              <div className="flex-1 h-px bg-gray-200" />
              <span className="text-xs font-medium text-gray-400 uppercase tracking-wide">or</span>
              <div className="flex-1 h-px bg-gray-200" />
            </div>
            <button
              type="button"
              onClick={() => googleLogin()}
              className="flex w-full items-center justify-center gap-3 rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm font-semibold text-gray-700 shadow-sm transition hover:bg-gray-50 hover:shadow active:scale-[0.98]"
            >
              <svg width="18" height="18" viewBox="0 0 48 48" fill="none">
                <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
                <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
                <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
                <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
              </svg>
              Continue with Google
            </button>
          </>
        ) : null}

        <p className="text-sm text-center mt-4 text-gray-600">
          Don't have an account?{" "}
          <Link to="/vendor/register" className="text-rose-600 font-semibold">
            Register Here
          </Link>
        </p>
      </div>
    </div>
  );
};

export default VendorLogin;
